import React from 'react';
import { motion } from 'framer-motion';
import { useFilterStore } from '../../store/filterStore';
import { pathologyFilters, msoFilters } from '../../utils/filterLogic';
import { useSound } from '../../hooks/useSound';
import { PlatformTargetIcon } from '../icons/PlatformTargetIcon';
import { AddonToolIcon } from '../icons/AddonToolIcon';
import clsx from 'clsx';

interface FilterCheckboxProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (id: string) => void;
  icon?: React.ReactNode;
}

const FilterCheckbox: React.FC<FilterCheckboxProps> = ({
  id,
  label,
  checked,
  onChange,
  icon,
}) => {
  const { play } = useSound();

  const handleChange = () => {
    play('filterClick');
    onChange(id);
  };

  return (
    <label className="flex items-start gap-3 cursor-pointer group mb-3">
      <div className="relative flex items-center justify-center w-5 h-5 mt-0.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          className="sr-only"
        />
        <div
          className={clsx(
            'w-5 h-5 border-2 rounded transition-all',
            checked
              ? 'bg-forest-green border-forest-green'
              : 'bg-navy-blue border-medium-gray group-hover:border-light-blue'
          )}
        >
          {checked && (
            <svg
              className="w-4 h-4 text-white"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      </div>
      <span className="flex items-center gap-2 text-sm text-light-gray group-hover:text-white">
        {icon}
        {label}
      </span>
    </label>
  );
};

// Get icon for a filter by ID
const getFilterIcon = (filterId: string): React.ReactNode | null => {
  if (filterId === 'strategy-platform') return <PlatformTargetIcon size={16} />;
  if (filterId === 'strategy-addon') return <AddonToolIcon size={16} />;
  if (filterId === 'mso-targets') return <PlatformTargetIcon size={16} />;
  return null;
};

export const FilterPanel: React.FC = () => {
  const { vertical, pendingFilters, toggleFilter, clearFilters, applyAllFilters } =
    useFilterStore();

  const isPathology = vertical === 'pathology';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="bg-navy-blue text-white px-4 py-3 -mx-6 -mt-6 mb-6">
        <h2 className="font-bold text-lg">
          {isPathology ? 'INVESTMENT CRITERIA' : 'MSO SELECTION CRITERIA'}
        </h2>
      </div>

      {isPathology ? (
        <>
          {/* Strategy Filters */}
          <div className="mb-4">
            <div className="text-xs text-forest-green font-bold mb-3 uppercase tracking-wide">
              Investment Strategy
            </div>
            {pathologyFilters.all
              .filter(f => f.category === 'strategy')
              .map(filter => (
                <FilterCheckbox
                  key={filter.id}
                  id={filter.id}
                  label={filter.label}
                  checked={pendingFilters.includes(filter.id)}
                  onChange={toggleFilter}
                  icon={getFilterIcon(filter.id)}
                />
              ))}
          </div>

          <hr className="border-medium-gray mb-4" />

          {/* Platform Criteria */}
          <div className="mb-4">
            <div className="text-xs text-light-blue font-bold mb-3 uppercase tracking-wide">
              Platform Criteria
            </div>
            {pathologyFilters.all
              .filter(f => f.category === 'platform')
              .map(filter => (
                <FilterCheckbox
                  key={filter.id}
                  id={filter.id}
                  label={filter.label}
                  checked={pendingFilters.includes(filter.id)}
                  onChange={toggleFilter}
                />
              ))}
          </div>

          <hr className="border-medium-gray mb-4" />

          {/* Add-on Criteria */}
          <div className="mb-6">
            <div className="text-xs text-light-blue font-bold mb-3 uppercase tracking-wide">
              Add-on Criteria
            </div>
            {pathologyFilters.all
              .filter(f => f.category === 'addon')
              .map(filter => (
                <FilterCheckbox
                  key={filter.id}
                  id={filter.id}
                  label={filter.label}
                  checked={pendingFilters.includes(filter.id)}
                  onChange={toggleFilter}
                />
              ))}
          </div>
        </>
      ) : (
        /* MSO Filters */
        <div className="space-y-2 mb-6">
          {msoFilters.map((filter) => (
            <FilterCheckbox
              key={filter.id}
              id={filter.id}
              label={filter.label}
              checked={pendingFilters.includes(filter.id)}
              onChange={toggleFilter}
              icon={getFilterIcon(filter.id)}
            />
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <motion.button
          className="w-full bg-forest-green text-white font-bold py-3 px-4 rounded-lg hover:bg-opacity-90 transition-all"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={applyAllFilters}
        >
          Apply Filters
        </motion.button>

        <motion.button
          className="w-full bg-white text-navy-blue font-bold py-3 px-4 rounded-lg border-2 border-navy-blue hover:bg-gray-50 transition-all"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={clearFilters}
        >
          Reset
        </motion.button>
      </div>

      {/* Filter Count */}
      {pendingFilters.length > 0 && (
        <div className="mt-4 text-sm text-medium-gray text-center">
          {pendingFilters.length} filter{pendingFilters.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
};
